import {
  ExitGuideButton,
  PreviousStepButton,
  StepHeader,
} from '../../components';
import nls from '../../../../nls/pt-BR.json';
import { z } from 'zod';
import {
  FieldArrayWithId,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AddAlert,
  Button,
  IconTrashCan,
  IcoPlusWithCircle,
} from '../../../../../../../component';
import { useDispatch, useSelector } from 'react-redux';
import {
  advanceToStep,
  setCurrentProgressTo,
  setLayoutsValuationPayload,
  setLayoutsValuationPrimaryResponse,
  studiesSelector,
} from '../../../../redux/studies.reducer';
import {
  DefaultLayoutFormValue,
  LayoutsFormData,
  layoutsFormSchema,
  LayoutValuationResponse,
  NUMERICAL_SELECTS,
} from '../../../../redux/studies.model';
import { postStudyStep } from '../../../../redux/studies.controller';
import { useTypedSelector } from '../../../../../../../hooks/useTypedSelector';
import {
  loaded,
  loading,
} from '../../../../../../../layout/redux/layoutActions';
import { MAX_BUILDING_LAYOUTS } from '../../../../redux/studies.constants';

const REALTY_TYPES = [
  { name: 'Apartamento', id: 'ap' },
  { name: 'Casa', id: 'cs' },
  { name: 'Comercial', id: 'comm' },
];

const DEFAULT_VALUE: DefaultLayoutFormValue = {
  id: '1',
  realtyType: 'ap',
  area: 1,
  bedrooms: '0',
  suites: '0',
  garages: '0',
};
const INITIAL_VALUE: LayoutsFormData = {
  layouts: [DEFAULT_VALUE],
};

export const BuildingLayouts: React.FC = () => {
  return (
    <>
      <div className="building-layouts">
        <p className="helper-text">
          {nls.studyCreationGuide.steps.step2.helper2}
        </p>
      </div>
      <BuildingLayoutsForm />
    </>
  );
};

const BuildingLayoutsForm: React.FC = () => {
  const dispatch = useDispatch();
  const { currentStep, id } = useTypedSelector(studiesSelector);

  const methods = useForm<LayoutsFormData>({
    resolver: zodResolver(layoutsFormSchema),
    defaultValues: INITIAL_VALUE,
  });
  const { fields, append, remove } = useFieldArray<LayoutsFormData>({
    control: methods.control,
    name: 'layouts',
  });
  const checkForNulls = (
    response: LayoutValuationResponse,
    payload: LayoutsFormData['layouts']
  ): void => {
    const payloadIds = payload.map((el) => el.id);
    const responseIds = response.map((el) => el.id);
    const nulls = payloadIds
      .filter((el) => !responseIds.includes(el))
      .join(', ');
    if (nulls) {
      dispatch(
        AddAlert(
          'warning',
          `Não há dados suficientes sobre as plantas nº: ${nulls}`
        )
      );
    }
  };
  const onSubmit = async (data: LayoutsFormData) => {
    try {
      const layoutsPayload = data.layouts.map((layout, index) => ({
        ...layout,
        id: (index + 1).toString(),
      }));
      dispatch(loading());
      const response = await postStudyStep(2, id, { layouts: layoutsPayload });
      checkForNulls(response.primary_market, layoutsPayload);
      dispatch([
        setLayoutsValuationPayload(layoutsPayload),
        setLayoutsValuationPrimaryResponse(response.primary_market),
        setCurrentProgressTo(2),
        AddAlert('success', 'Avaliação do mercado primário concluída'),
        advanceToStep(currentStep.nextStep),
        loaded(),
      ]);
    } catch (_) {
      dispatch(loaded());
    }
  };
  const add = () => {
    if (fields.length < MAX_BUILDING_LAYOUTS) {
      append(DEFAULT_VALUE);
    } else {
      dispatch(
        AddAlert('warning', `Número máximo de plantas: ${MAX_BUILDING_LAYOUTS}`)
      );
    }
  };
  const removeAt = (index: number) => {
    remove(index);
  };
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="building-layouts__form-container">
          {fields.map((field, fieldIndex) => (
            <BuildingLayoutsFormItem
              field={field}
              fieldIndex={fieldIndex}
              key={field.id}
              callbackRemoveItem={removeAt}
            />
          ))}
        </div>
        <button
          className="link normal add-new-form-item"
          onClick={add}
          data-cy="AddNewLayoutFormItem"
          style={{ border: '1px solid #925AC4', borderRadius: 4 }}
          type="button"
        >
          <IcoPlusWithCircle
            style={{ color: '#925AC4' }}
            cy="AddNewLayoutFormItem"
          />
        </button>
        <div className="study-creation-guide__buttons-container">
          <div>
            <ExitGuideButton />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <PreviousStepButton />
            <button
              className="btn normal primary"
              data-cy="StudyCreationGuideNextStep"
              type="submit"
            >
              {nls.studyCreationGuide.buttons.evaluate}
            </button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

interface FormItemProps {
  fieldIndex: number;
  field: FieldArrayWithId<LayoutsFormData, 'layouts', 'id'>;
  callbackRemoveItem: (index: number) => void;
}

const BuildingLayoutsFormItem: React.FC<FormItemProps> = ({
  fieldIndex,
  field,
  callbackRemoveItem,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<LayoutsFormData>();
  const hasErrors = (inputName: keyof typeof DEFAULT_VALUE) => {
    return errors?.layouts?.[fieldIndex]?.[inputName];
  };
  return (
    <div key={field.id} style={{ marginBottom: 12 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <h6 className="layout-number">
          {nls.studyCreationGuide.steps.step2.form.layoutNumber}{' '}
          {fieldIndex + 1}
        </h6>
        {fieldIndex > 0 && (
          <Button
            cy="RemoveBuildingLayoutsFormItem"
            onClick={() => callbackRemoveItem(fieldIndex)}
            className="link normal"
          >
            <IconTrashCan
              style={{ color: '#D31717' }}
              cy="RemoveBuildingLayoutsFormItem"
            />
          </Button>
        )}
      </div>

      <div className="building-layouts__form-container__form-element">
        <label htmlFor="realtyType">
          {nls.studyCreationGuide.steps.step2.form.realtyType}
        </label>
        <select
          {...register(`layouts.${fieldIndex}.realtyType`)}
          defaultValue="placeholder"
          className={`${hasErrors('realtyType') ? 'with-errors' : ''}`}
        >
          <option value="placeholder" disabled style={{ color: '#C4C4C4' }}>
            {nls.studyCreationGuide.steps.step2.form.realtyTypePlaceholder}
          </option>
          {REALTY_TYPES.map((el, index) => (
            <option key={index} value={el.id}>
              {el.name}
            </option>
          ))}
        </select>
      </div>
      <div className="building-layouts__form-container__form-element">
        <label htmlFor="area">
          {nls.studyCreationGuide.steps.step2.form.area}
        </label>
        <input
          type="number"
          {...register(`layouts.${fieldIndex}.area`, {
            valueAsNumber: true,
          })}
          placeholder={nls.studyCreationGuide.steps.step2.form.areaPlaceholder}
          className={`${hasErrors('area') ? 'with-errors' : ''} default-input`}
        />
      </div>
      <div className=" numerical-containers">
        <div className="building-layouts__form-container__form-element">
          <label htmlFor="bedrooms">
            {nls.studyCreationGuide.steps.step2.form.bedrooms}
          </label>
          <select
            {...register(`layouts.${fieldIndex}.bedrooms`)}
            className={`${hasErrors('bedrooms') ? 'with-errors' : ''}`}
          >
            {NUMERICAL_SELECTS.map((el, index) => (
              <option key={index} value={el}>
                {el}
              </option>
            ))}
          </select>
        </div>
        <div className="building-layouts__form-container__form-element">
          <label htmlFor="suites">
            {nls.studyCreationGuide.steps.step2.form.suites}
          </label>
          <select
            {...register(`layouts.${fieldIndex}.suites`)}
            className={`${hasErrors('suites') ? 'with-errors' : ''}`}
          >
            {NUMERICAL_SELECTS.map((el, index) => (
              <option key={index} value={el}>
                {el}
              </option>
            ))}
          </select>
        </div>
        <div className="building-layouts__form-container__form-element">
          <label htmlFor="garages">
            {nls.studyCreationGuide.steps.step2.form.garages}
          </label>
          <select
            {...register(`layouts.${fieldIndex}.garages`)}
            className={`${hasErrors('garages') ? 'with-errors' : ''}`}
          >
            {NUMERICAL_SELECTS.map((el, index) => (
              <option key={index} value={el}>
                {el}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};
